<template>
  <div class="requests-page cabinet-page" :class="{ 'is-loading': loading }">
    <cabinet-page-header :eyebrow="isHistory ? $t('common.archive') : $t('requests.employeeService')" :title="isHistory ? $t('requests.historyTitle') : $t('requests.title')" :subtitle="isHistory ? $t('requests.historySubtitle') : $t('requests.activeSubtitle')" />
    <div v-if="!isHistory" class="requests-tabs">
      <button v-for="tab in tabs" :key="tab.scope" :class="{ active: currentScope === tab.scope }" @click="currentScope = tab.scope">{{ $t(tab.labelKey) }}</button>
    </div>
    <app-page-loader v-if="loading" />
    <cabinet-state v-else-if="loadError" type="error" :title="$t('requests.unavailable')" :message="$t('common.pleaseRetry')" @retry="loadRequests" />
    <cabinet-state v-else-if="!requests.length" type="empty" :title="isHistory ? $t('requests.noCompleted') : $t('common.noRequests')" :message="$t('common.requestsAppear')" />
    <div v-else class="requests-list">
      <router-link v-for="request in requests" :key="request.id" :to="detailRoute(request)" class="request-card">
        <div class="request-card__icon">
          <img v-if="request.source.code === 'staff_loan'" src="@/assets/img/employee/loan-wallet.svg" alt="">
          <template v-else>{{ sourceIcon(request.source.code) }}</template>
        </div>
        <div class="request-card__body">
          <div><strong>{{ $choice('sources', request.source) }}</strong><cabinet-status :status="request.status" /></div>
          <p>{{ summaryText(request) }}</p><small>{{ formatDate(request.created_at) }}</small>
        </div><span class="request-card__arrow">›</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { getPersonalCabinetRequests } from '@/http'
import { formatLocalizedDate } from '@/i18n'
import CabinetPageHeader from './CabinetPageHeader'
import CabinetState from './CabinetState'
import CabinetStatus from './CabinetStatus'

export default {
  name: 'PersonalCabinetRequests',
  components: { CabinetPageHeader, CabinetState, CabinetStatus },
  props: { scope: { type: String, default: '' } },
  data() { return { currentScope: this.scope || 'active', requests: [], loading: true, loadError: false, tabs: [{ labelKey: 'common.active', scope: 'active' }, { labelKey: 'common.archive', scope: 'history' }, { labelKey: 'common.all', scope: 'all' }] } },
  computed: { isHistory() { return this.scope === 'history' } },
  watch: { currentScope: 'loadRequests' },
  created() { this.loadRequests() },
  methods: {
    loadRequests() {
      this.loading = true
      this.loadError = false
      return getPersonalCabinetRequests(this.isHistory ? 'history' : this.currentScope)
        .then(({ data }) => {
          const items = Array.isArray(data) ? data : (data.results || [])
          this.requests = items.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    detailRoute(request) { return { name: 'personal-cabinet-request-detail', params: { requestId: request.id }, query: { source: request.source.code, sourceId: request.source_id } } },
    sourceIcon(source) { return { advance: '↗', staff_loan: '◇', leave: '☀', idea: '✦', misconduct: '!' }[source] || '▤' },
    summaryText(request) {
      const summary = request.summary || {}
      return summary.text || summary.reason || summary.period || summary.amount || this.$t('common.openDetails')
    },
    formatDate(value) { return value ? formatLocalizedDate(value, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
  },
}
</script>

<style lang="sass" scoped>
.requests-tabs
  display: inline-flex
  gap: 4px
  margin-bottom: 20px
  padding: 4px
  border-radius: 22px
  background: #ede9de
  button
    min-width: 74px
    padding: 8px 15px
    border-radius: 18px
    color: #7d7a72
    font-size: 12px
    font-weight: 600
    &.active
      background: #fff
      color: #35362e
      box-shadow: 0 3px 9px rgba(57, 54, 44, .08)
.requests-list
  display: grid
  gap: 10px
.request-card
  display: grid
  grid-template-columns: 44px minmax(0, 1fr) 12px
  gap: 12px
  align-items: center
  padding: 15px
  border: 1px solid #e8e2d6
  border-radius: 18px
  background: #fffefa
  color: #34352e
.request-card__icon
  display: grid
  place-items: center
  width: 44px
  height: 44px
  border-radius: 14px
  background: #efebdf
  color: #8e7d2d
  font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
  font-size: 21px
  font-weight: 600
  img
    display: block
    width: 34px
    height: 30px
    object-fit: contain
.request-card__body
  min-width: 0
  > div
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 8px
  strong
    font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
    font-size: 16px
    font-weight: 600
    line-height: 1.3
  p
    margin-top: 6px
    color: #6f6d66
    font-size: 12px
    line-height: 1.35
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
  small
    display: block
    margin-top: 5px
    color: #a09d95
    font-size: 9px
.request-card__arrow
  color: #aaa69b
  font-size: 25px
</style>
